import { useCallback, useMemo } from 'react';
import {
  useLowStockItems,
  useStockLevels,
  useMaterialCatalog,
} from '@hooks/api/warehouse';
import { MaterialCatalog, StockLevel } from '@models/warehouse.model';

export interface AlertItem {
  material: MaterialCatalog;
  stockLevel: StockLevel;
  onHand: number;
  reorderPoint: number;
  suggestedOrder: number;
  percentOfReorder: number;
  isCritical: boolean;
}

export interface WarehouseAlertGroup {
  warehouseId: string;
  name: string;
  items: AlertItem[];
}

/**
 * Shared low-stock alert data for the Warehouse Alerts page and the dashboard
 * Warehouse alerts slide-over. Builds `AlertItem`s from low-stock stock levels +
 * the material catalog and groups them by warehouse (critical/out-of-stock items
 * surfaced first within each group), matching the global sidebar badge count.
 */
export const useLowStockAlerts = () => {
  const {
    data: lowStockItems = [],
    isLoading: lowStockLoading,
    isError: lowStockError,
    refetch: refetchLowStock,
  } = useLowStockItems();

  const {
    isLoading: stockLevelsLoading,
    isError: stockLevelsError,
    refetch: refetchStockLevels,
  } = useStockLevels();

  const {
    data: materials = [],
    isLoading: materialsLoading,
    isError: materialsError,
  } = useMaterialCatalog();

  // Consider errors as "done loading" to not block the UI
  const isLoading =
    (lowStockLoading && !lowStockError) ||
    (stockLevelsLoading && !stockLevelsError) ||
    (materialsLoading && !materialsError);

  const alertItems = useMemo<AlertItem[]>(() => {
    return lowStockItems
      .map((stockLevel) => {
        const material = materials.find(
          (m) => m.catalogId === stockLevel.catalogId,
        );
        const onHand = stockLevel.quantityOnHand || 0;
        const reorderPoint = material?.reorderPoint || 0;
        const suggestedOrder =
          material?.suggestedOrderQuantity || reorderPoint * 2;
        const percentOfReorder =
          reorderPoint > 0 ? Math.round((onHand / reorderPoint) * 100) : 0;
        const isCritical = onHand <= 0;

        return {
          material: material || {
            catalogId: stockLevel.catalogId,
            name: 'Unknown Material',
            isActive: true,
            averageCost: 0,
            organizationId: '',
          },
          stockLevel,
          onHand,
          reorderPoint,
          suggestedOrder,
          percentOfReorder,
          isCritical,
        };
      })
      .filter((item) => item.material);
  }, [lowStockItems, materials]);

  // Group alerts by warehouse so each item's origin is clear (multiple warehouses).
  // Critical (out of stock) items are surfaced first within each warehouse group.
  const warehouseGroups = useMemo<WarehouseAlertGroup[]>(() => {
    const map = new Map<string, WarehouseAlertGroup>();
    alertItems.forEach((item) => {
      const warehouseId = item.stockLevel.warehouseId || 'unassigned';
      const name = item.stockLevel.warehouse?.name || 'Unassigned warehouse';
      if (!map.has(warehouseId)) {
        map.set(warehouseId, { warehouseId, name, items: [] });
      }
      map.get(warehouseId)!.items.push(item);
    });
    return Array.from(map.values()).map((group) => ({
      ...group,
      items: group.items
        .slice()
        .sort((a, b) => Number(b.isCritical) - Number(a.isCritical)),
    }));
  }, [alertItems]);

  const refetch = useCallback(() => {
    refetchLowStock();
    refetchStockLevels();
  }, [refetchLowStock, refetchStockLevels]);

  return { alertItems, warehouseGroups, isLoading, refetch };
};

export const formatAlertNumber = (value: number): string =>
  new Intl.NumberFormat('en-US').format(value);
